import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { toast } from '@/hooks/use-toast';
import { Clock, Target, Zap } from 'lucide-react';
import { SESSION_LENGTH_MAP } from '@/utils/sessionLengthProcessor';

const sessionLengthOptions = [
  { value: '3 minutes', label: '3 minutes - Quick zen session' },
  { value: '5 minutes', label: '5 minutes - Standard session' },
  { value: '7 minutes', label: '7 minutes - Deep practice' }
];

const difficultyOptions = [
  { value: 'beginner', label: 'Beginner - Simple guidance' },
  { value: 'intermediate', label: 'Intermediate - Balanced approach' },
  { value: 'advanced', label: 'Advanced - Minimal guidance' }
];

export const SessionPreferences = () => {
  const { 
    preferences, 
    updatePreference, 
    updatingPreference 
  } = useOptimizedUserPreferences();

  const currentLength = preferences?.find(p => p.preference_type === 'session_length')?.preference_value || '5 minutes';
  const currentDifficulty = preferences?.find(p => p.preference_type === 'difficulty_level')?.preference_value || 'intermediate';
  const autoStart = preferences?.find(p => p.preference_type === 'auto_start')?.preference_value === 'true';

  const handleLengthChange = async (value: string) => {
    try {
      await updatePreference({
        category: 'sessions',
        preferenceType: 'session_length',
        value: value,
        confidenceBoost: 0.9
      });
      toast({
        title: "Session length updated",
        description: `Default session length set to ${value}`,
      });
    } catch (error) {
      toast({
        title: "Error updating session length",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handleDifficultyChange = async (value: string) => {
    try {
      await updatePreference({
        category: 'sessions',
        preferenceType: 'difficulty_level',
        value: value,
        confidenceBoost: 0.8
      });
      toast({
        title: "Difficulty level updated",
        description: `Difficulty set to ${value}`,
      });
    } catch (error) {
      toast({
        title: "Error updating difficulty level",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  const handleAutoStartChange = async (enabled: boolean) => {
    try {
      await updatePreference({
        category: 'sessions',
        preferenceType: 'auto_start',
        value: enabled ? 'true' : 'false',
        confidenceBoost: 0.9
      });
      toast({
        title: "Auto-start preference updated",
        description: `Sessions will ${enabled ? 'start automatically' : 'wait for manual start'}`,
      });
    } catch (error) {
      toast({
        title: "Error updating auto-start preference",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Session Length */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Default Session Length
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="length-select">Preferred Session Duration</Label>
            <Select value={currentLength} onValueChange={handleLengthChange} disabled={updatingPreference}>
              <SelectTrigger>
                <SelectValue placeholder="Select session length" />
              </SelectTrigger>
              <SelectContent>
                {sessionLengthOptions.map((length) => (
                  <SelectItem key={length.value} value={length.value}>
                    {length.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Difficulty Level */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" />
            Meditation Difficulty
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="difficulty-select">Guidance Level</Label>
            <Select value={currentDifficulty} onValueChange={handleDifficultyChange} disabled={updatingPreference}>
              <SelectTrigger>
                <SelectValue placeholder="Select difficulty level" />
              </SelectTrigger>
              <SelectContent>
                {difficultyOptions.map((difficulty) => (
                  <SelectItem key={difficulty.value} value={difficulty.value}>
                    {difficulty.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Auto-start Sessions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Session Behavior
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="auto-start-toggle">Auto-start Sessions</Label>
              <p className="text-sm text-muted-foreground">
                Start sessions automatically after generation completes
              </p>
            </div>
            <Switch
              id="auto-start-toggle"
              checked={autoStart}
              onCheckedChange={handleAutoStartChange}
              disabled={updatingPreference}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};